import React, { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import t from 'react-translate';
import { Button } from 'react-bootstrap';
import { NvPopover } from 'shared/components/nv-popover';
import NvTextInput from 'shared/components/inputs/nv-text-input';
import { halfSpacing, standardSpacing, threeQuartersSpacing } from 'styles/global_defaults/scaffolding';
import { gray5 } from 'styles/global_defaults/colors';
import { textExtraLargeFontSize, textMediumFontSize, semiBoldFontWeight } from 'styles/global_defaults/fonts';
import { LearnerProgressFilters } from './filters-flyout-modal';

const getDefaultViewName = () => t.LEARNER_PROGRESS.FILTERS.DEFAULT_VIEW_NAME();

type FilterResultsBannerProps = {
  filters: LearnerProgressFilters;
  onClear: () => void;
  onSaveView: (viewName: string, filters: LearnerProgressFilters) => void;
};

const styles = css`
  border-bottom: 1px solid ${gray5};
  padding: ${standardSpacing}px;

  .banner-title {
    font-size: ${textExtraLargeFontSize}px;
  }

  .banner-actions {
    gap: ${halfSpacing}px;
  }
`;

const popoverStyles = css`
  max-width: 400px;
`;

const saveViewFormStyles = css`
  padding: ${halfSpacing}px;

  .save-view-title {
    text-align: center;
    font-size: ${textMediumFontSize}px;
    font-weight: ${semiBoldFontWeight};
    margin-bottom: ${threeQuartersSpacing}px;
  }

  .save-view-description {
    margin-bottom: ${standardSpacing}px;
  }

  .save-view-actions {
    gap: ${halfSpacing}px;
  }
`;

const buildFilterDescription = (filters: LearnerProgressFilters): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  if (filters.assignmentsEnabled && filters.assignmentName) {
    parts.push(
      <span key='assignment'>
        {t.LEARNER_PROGRESS.FILTERS.HAS_NOT_COMPLETED_PREFIX()} <strong>{filters.assignmentName}</strong>
      </span>,
    );
  }
  if (filters.inactiveForEnabled && filters.inactiveFor) {
    const hasAssignment = filters.assignmentsEnabled && filters.assignmentName;
    parts.push(
      <span key='inactive'>
        {hasAssignment
          ? t.LEARNER_PROGRESS.FILTERS.INACTIVE_DETAIL(filters.inactiveFor.toString())
          : t.LEARNER_PROGRESS.FILTERS.INACTIVE_DETAIL_STANDALONE(filters.inactiveFor.toString())}
      </span>,
    );
  }
  return parts;
};

const FilterResultsBanner = ({ filters, onClear, onSaveView }: FilterResultsBannerProps) => {
  const [showSavePopover, setShowSavePopover] = useState(false);
  const [viewName, setViewName] = useState(getDefaultViewName());
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showSavePopover) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [showSavePopover]);

  const descriptionParts = buildFilterDescription(filters);

  const handleSave = () => {
    if (!viewName.trim()) return;
    onSaveView(viewName.trim(), filters);
    setShowSavePopover(false);
    setViewName(getDefaultViewName());
  };

  const handleCancel = () => {
    setShowSavePopover(false);
    setViewName(getDefaultViewName());
  };

  return (
    <div css={styles} className='text-center' data-qa='LEARNER_PROGRESS_FILTERS_RESULTS_BANNER'>
      <div className='banner-title mb-2'>
        {t.LEARNER_PROGRESS.FILTERS.FILTERED_RESULTS_FOR()}
      </div>
      <div className='text-regular mb-2'>
        {descriptionParts.reduce<React.ReactNode[]>((acc, part, i, arr) => {
          acc.push(part);
          if (i < arr.length - 1) acc.push(t.JOIN.TWO.AND());
          return acc;
        }, [])}
      </div>
      <div className='banner-actions d-flex justify-content-center'>
        <Button
          variant='outline-primary'
          onClick={onClear}
          data-qa='LEARNER_PROGRESS_FILTERS_CLEAR_BUTTON'
        >
          {t.LEARNER_PROGRESS.FILTERS.CLEAR()}
        </Button>
        <NvPopover
          show={showSavePopover}
          placement='bottom'
          rootClose
          onHide={handleCancel}
          overlayStyles={popoverStyles}
          preventOverflow
          content={(
            <div css={saveViewFormStyles}>
              <div className='save-view-title'>
                {t.LEARNER_PROGRESS.SAVED_VIEWS.SAVE_VIEW_TITLE()}
              </div>
              <div className='save-view-description text-regular'>
                {t.LEARNER_PROGRESS.SAVED_VIEWS.SAVE_VIEW_DESCRIPTION()}
              </div>
              <NvTextInput
                ref={inputRef}
                value={viewName}
                onChange={(e) => setViewName(e.target.value)}
                placeholder={t.LEARNER_PROGRESS.SAVED_VIEWS.VIEW_NAME_PLACEHOLDER()}
                ariaLabel={t.LEARNER_PROGRESS.SAVED_VIEWS.VIEW_NAME_PLACEHOLDER()}
                data-qa='LEARNER_PROGRESS_FILTERS_SAVE_VIEW_NAME_INPUT'
              />
              <div className='save-view-actions d-flex justify-content-center mt-3'>
                <Button
                  size='sm'
                  variant='outline-primary'
                  onClick={handleCancel}
                  data-qa='LEARNER_PROGRESS_FILTERS_SAVE_VIEW_CANCEL_BUTTON'
                >
                  {t.FORM.CANCEL()}
                </Button>
                <Button
                  size='sm'
                  variant='primary'
                  onClick={handleSave}
                  disabled={!viewName.trim()}
                  data-qa='LEARNER_PROGRESS_FILTERS_SAVE_VIEW_CONFIRM_BUTTON'
                >
                  {t.LEARNER_PROGRESS.SAVED_VIEWS.SAVE()}
                </Button>
              </div>
            </div>
          )}
        >
          <Button
            variant='primary'
            onClick={() => setShowSavePopover(true)}
            data-qa='LEARNER_PROGRESS_FILTERS_SAVE_VIEW_BUTTON'
          >
            {t.LEARNER_PROGRESS.FILTERS.SAVE_THIS_VIEW()}
          </Button>
        </NvPopover>
      </div>
    </div>
  );
};

export default FilterResultsBanner;
